import { useEffect, useRef, useState } from 'react';
import type { ServiceLandingData } from '../../../mocks/service-landings';

interface SLProcessProps {
  data: ServiceLandingData;
}

function useInView() {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return { ref, inView };
}

export default function SLProcess({ data }: SLProcessProps) {
  const { ref, inView } = useInView();

  return (
    <section className="py-24 bg-zinc-50">
      <div className="max-w-6xl mx-auto px-6">
        <div ref={ref}>
          {/* Header */}
          <div
            className={`text-center mb-16 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <div className="inline-flex items-center gap-2 bg-white border border-zinc-200 rounded-full px-4 py-1.5 mb-5">
              <i className="ri-map-pin-range-line text-zinc-500 text-sm" />
              <span className="text-zinc-600 text-sm font-semibold">Postup spolupráce</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-zinc-900 mb-4 leading-tight">
              {data.process.heading}
            </h2>
          </div>

          {/* Steps */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {data.process.steps.map((step, i) => (
              <div
                key={step.number}
                className={`relative bg-white border border-zinc-100 rounded-2xl p-7 transition-all duration-600 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${i * 120 + 150}ms` }}
              >
                {/* Connector line */}
                {i < data.process.steps.length - 1 && (
                  <div className="hidden lg:block absolute top-12 -right-3 w-6 z-10">
                    <i className="ri-arrow-right-line text-zinc-300 text-xl" />
                  </div>
                )}

                {/* Number */}
                <span className="block text-5xl font-black text-zinc-100 mb-4 leading-none select-none">{step.number}</span>

                {/* Orange accent line */}
                <div className="w-8 h-1 bg-gradient-to-r from-orange-500 to-amber-400 rounded-full mb-4" />

                <h3 className="text-zinc-900 font-bold text-base mb-2 leading-tight">{step.title}</h3>
                <p className="text-zinc-500 text-sm leading-relaxed">{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
